import React, { useState, useEffect } from 'react';
import { Box, Typography, Divider, CircularProgress, FormGroup, Button } from '@mui/material';
import SidePanel from './SidePanel';
import { CreateButton, InputField, Label, TextArea, ModalTitle } from './SharedComponents';
import { SidePanelInterface } from '../types';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { ArrowLeftIcon } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationModal from './ConfirmationModal';
import { useSelector } from 'react-redux';
import { getCurrency } from '../services/tableHelper';
import apiClient from '../services/apiClient';
import notify from '../services/notify';
import ReactGA from 'react-ga4';
import { AxiosError } from 'axios';

interface CustomerInterface {
	id?: number;
	name: string;
	email?: string | null;
	phone?: string | null;
	address?: string | null;
	notes?: string | null;
	date_created?: string;
	date_updated?: string;
	pendingInvoices?: any[];
	totalAmountOwed?: number;
}

interface CustomerModalProps extends Omit<SidePanelInterface, 'children'> {
	customer?: CustomerInterface | null;
	isNewCustomer?: boolean;
	onCustomerUpdate?: (customer: CustomerInterface) => void;
	onCustomerDelete?: (customerId: number) => void;
}

const CustomerModal: React.FC<CustomerModalProps> = ({
	customer,
	handleHidePanel,
	isNewCustomer = false,
	onCustomerUpdate,
	onCustomerDelete,
	...sidePanelProps
}) => {
	const currency = useSelector((state: any) => getCurrency(state.auth.user.currency));

	const [mode, setMode] = useState<'view' | 'edit'>(isNewCustomer ? 'edit' : 'view');
	const [customerName, setCustomerName] = useState<string>('');
	const [customerEmail, setCustomerEmail] = useState<string>('');
	const [customerPhone, setCustomerPhone] = useState<string>('');
	const [customerAddress, setCustomerAddress] = useState<string>('');
	const [customerNotes, setCustomerNotes] = useState<string>('');
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
	const [isDeleting, setIsDeleting] = useState<boolean>(false);
	const [invoicesLoading, setInvoicesLoading] = useState<boolean>(false);
	const [pendingInvoices, setPendingInvoices] = useState<any[] | null>(null);
	const [totalAmountOwed, setTotalAmountOwed] = useState<number>(0);

	useEffect(() => {
		// Reset mode when modal opens/changes customer
		setMode(isNewCustomer ? 'edit' : 'view');

		if (customer) {
			setCustomerName(customer.name || '');
			setCustomerEmail(customer.email || '');
			setCustomerPhone(customer.phone || '');
			setCustomerAddress(customer.address || '');
			setCustomerNotes(customer.notes || '');
		} else {
			setCustomerName('');
			setCustomerEmail('');
			setCustomerPhone('');
			setCustomerAddress('');
			setCustomerNotes('');
		}

		// Reset invoice data when customer changes
		setPendingInvoices(null);
		setTotalAmountOwed(0);
	}, [customer, isNewCustomer]);

	// Fetch invoices when modal opens with a customer
	useEffect(() => {
		if (customer?.id && sidePanelProps.isVisible && !isNewCustomer) {
			fetchCustomerInvoices(customer.id);
		}
	}, [customer?.id, sidePanelProps.isVisible, isNewCustomer]);

	const fetchCustomerInvoices = async (customerId: number) => {
		setInvoicesLoading(true);
		try {
			const customerData = await apiClient.get(`/customers/${customerId}/invoices`);
			setPendingInvoices(customerData.pendingInvoices || []);
			setTotalAmountOwed(customerData.totalAmountOwed || 0);
		} catch (error) {
			console.error('Error fetching customer invoices:', error);
			setPendingInvoices([]);
			setTotalAmountOwed(0);
		} finally {
			setInvoicesLoading(false);
		}
	};

	const handleChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const value = event.target.value;
		switch (field) {
			case 'name':
				setCustomerName(value);
				break;
			case 'email':
				setCustomerEmail(value);
				break;
			case 'phone':
				setCustomerPhone(value);
				break;
			case 'address':
				setCustomerAddress(value);
				break;
			case 'notes':
				setCustomerNotes(value);
				break;
		}
	};

	const hidePanel = (customer: CustomerInterface | null = null) => {
		setCustomerName('');
		setCustomerEmail('');
		setCustomerPhone('');
		setCustomerAddress('');
		setCustomerNotes('');
		setMode(isNewCustomer ? 'edit' : 'view');

		if (customer) {
			handleHidePanel(customer);
		} else {
			handleHidePanel();
		}
	};

	const isFieldsValid = () => {
		if (customerName.trim().length === 0) {
			notify('error', 'Name is required');
			return false;
		}

		if (customerEmail && !/\S+@\S+\.\S+/.test(customerEmail)) {
			notify('error', 'Invalid email address');
			return false;
		}

		return true;
	};

	const handleSave = async () => {
		if (!isFieldsValid()) return;

		setIsLoading(true);
		const customerId = customer?.id;

		const customerData = {
			name: customerName.trim(),
			email: customerEmail.trim() || null,
			phone: customerPhone.trim() || null,
			address: customerAddress.trim() || null,
			notes: customerNotes.trim() || null,
		};

		try {
			let result;
			if (customerId) {
				// Update existing customer
				result = await apiClient.update(`/customers/${customerId}`, customerData);
				notify('success', 'Customer updated successfully');

				ReactGA.event({
					category: 'Customer',
					action: 'Updated Customer',
					label: `Customer: ${customerData.name}`,
				});
			} else {
				// Create new customer
				result = await apiClient.post('/customers', customerData);
				notify('success', 'Customer created successfully');

				ReactGA.event({
					category: 'Customer',
					action: 'Created Customer',
					label: `Customer: ${customerData.name}`,
				});
			}

			setIsLoading(false);

			if (onCustomerUpdate) {
				onCustomerUpdate(result.data);
			}

			hidePanel(result.data);
		} catch (error) {
			setIsLoading(false);
			if (error instanceof AxiosError) {
				notify('error', error.response?.data?.message || 'Failed to save customer');
			} else {
				notify('error', 'Failed to save customer');
			}
		}
	};

	const handleDelete = () => {
		if (!customer?.id) return;

		setIsDeleting(true);
		apiClient
			.delete(`/customers/${customer.id}`)
			.then(() => {
				setIsDeleting(false);
				setIsDeleteDialogOpen(false);
				notify('success', 'Customer deleted successfully');

				ReactGA.event({
					category: 'Customer',
					action: 'Deleted Customer',
					label: `Customer: ${customer.name}`,
				});

				if (onCustomerDelete && customer.id) {
					onCustomerDelete(customer.id);
				}

				hidePanel();
			})
			.catch((error) => {
				setIsDeleting(false);
				setIsDeleteDialogOpen(false);
				if (error instanceof AxiosError) {
					notify('error', error.response?.data?.message || 'Failed to delete customer');
				} else {
					notify('error', 'Failed to delete customer');
				}
			});
	};

	const handleViewInvoice = (invoice: any) => {
		// Use the same URL structure as OrderModal
		if (!invoice || !invoice.hash_id) {
			console.error('Invoice or hash_id is missing', invoice);
			return;
		}
		const invoiceUrl = `/invoice?hash=${invoice.hash_id}&skipTracking=true`;
		window.open(invoiceUrl, '_blank');
	};

	const formatCurrency = (amount: number) => {
		return `${currency}${Number(amount).toFixed(2)}`;
	};

	const headerComponents = [];

	if (mode === 'view') {
		headerComponents.push(
			<CreateButton key="edit-button" onClick={() => setMode('edit')} outline>
				<ModeEditOutlinedIcon style={{ fontSize: '17px' }} />
				<span>Edit Customer</span>
			</CreateButton>
		);
	}

	if (mode === 'edit' && !isNewCustomer && customer) {
		headerComponents.push(
			<CreateButton key="cancel-button" onClick={() => setMode('view')} outline>
				<ArrowLeftIcon style={{ fontSize: '17px' }} />
				<span>Cancel Edit</span>
			</CreateButton>
		);
	}

	return (
		<>
			<SidePanel {...sidePanelProps} handleHidePanel={() => hidePanel()} headerComponents={headerComponents}>
				{mode === 'edit' && isNewCustomer && <ModalTitle>Add Customer</ModalTitle>}

				{mode === 'view' && customer && (
					<>
						{/* Customer Details - Always shown in view mode */}
						<Box sx={{ mb: 3 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Customer Name
							</Typography>
							<Typography variant="h6">{customerName}</Typography>
						</Box>

						<Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
							<Box sx={{ flex: 1 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Email
								</Typography>
								<Typography variant="body1">{customerEmail || '-'}</Typography>
							</Box>

							<Box sx={{ flex: 1 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Phone
								</Typography>
								<Typography variant="body1">{customerPhone || '-'}</Typography>
							</Box>
						</Box>

						{customerAddress && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Address
								</Typography>
								<Typography variant="body1">{customerAddress}</Typography>
							</Box>
						)}

						{customerNotes && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Notes
								</Typography>
								<Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
									{customerNotes}
								</Typography>
							</Box>
						)}

						{customer.date_created && (
							<Box sx={{ mb: 3 }}>
								<Typography variant="subtitle2" color="text.secondary">
									Customer Since
								</Typography>
								<Typography variant="body1">
									{new Date(customer.date_created).toLocaleDateString()}
								</Typography>
							</Box>
						)}

						<Divider sx={{ my: 2 }} />

						{/* Unpaid Orders Section */}
						<Box sx={{ mb: 2 }}>
							<Typography variant="h6">Orders Pending Payment</Typography>
						</Box>

						<Box sx={{ mb: 4 }}>
							{invoicesLoading ? (
								<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
									<CircularProgress size={30} />
								</Box>
							) : (
								<>
									{pendingInvoices && pendingInvoices.length > 0 && (
										<Box
											sx={{
												display: 'grid',
												gridTemplateColumns: '2fr 1.5fr 1fr 1fr 0.5fr',
												gap: '10px',
												alignItems: 'center',
												mb: '10px',
												px: '10px',
											}}
										>
											<Typography sx={{ fontSize: '14px' }}>Invoice #</Typography>
											<Typography sx={{ fontSize: '14px' }}>Date</Typography>
											{/* <Typography sx={{ fontSize: '14px', textAlign: 'right' }}>Total</Typography> */}
											<Typography sx={{ fontSize: '14px', textAlign: 'right' }}>
												Amount Due
											</Typography>
											<Typography sx={{ fontSize: '14px' }}></Typography>
										</Box>
									)}

									<Box>
										{pendingInvoices && pendingInvoices.length > 0 ? (
											pendingInvoices.map((invoice: any) => (
												<Box
													key={invoice.id}
													sx={{
														mb: '5px',
														p: '10px',
														display: 'grid',
														gridTemplateColumns: '2fr 1.5fr 1fr 1fr 0.5fr',
														gap: '10px',
														alignItems: 'center',
														borderTop: '1px solid #e5e7eb',
														'&:hover': {
															backgroundColor: '#f9fafb',
														},
													}}
												>
													<Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
														{invoice.hash_id.substring(0, 8)}...
													</Typography>
													<Typography sx={{ fontSize: '14px' }}>
														{new Date(
															invoice.backdated_order_date || invoice.date_created
														).toLocaleDateString()}
													</Typography>
													{/* <Typography sx={{ fontSize: '14px', textAlign: 'right' }}>
                                                        {formatCurrency(invoice.total_price || 0)}
                                                    </Typography> */}
													<Typography
														sx={{
															fontSize: '14px',
															textAlign: 'right',
															color: 'error.main',
														}}
													>
														{formatCurrency(invoice.total_price || 0)}
													</Typography>
													<Button
														size="small"
														onClick={() => handleViewInvoice(invoice)}
														sx={{
															fontSize: '12px',
															padding: '2px 8px',
															minWidth: 'unset',
														}}
													>
														View
													</Button>
												</Box>
											))
										) : (
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ textAlign: 'center', py: 2 }}
											>
												No unpaid orders
											</Typography>
										)}
									</Box>

									{pendingInvoices && pendingInvoices.length > 0 && (
										<Box
											sx={{
												p: '10px',
												display: 'flex',
												flexDirection: 'row',
												justifyContent: 'space-between',
												mt: '10px',
												borderTop: '1px solid #e5e7eb',
											}}
										>
											<Typography variant="h6">Total Due</Typography>
											<Typography variant="h6" color="error">
												{formatCurrency(totalAmountOwed)}
											</Typography>
										</Box>
									)}
								</>
							)}
						</Box>
					</>
				)}

				{mode === 'edit' && (
					<>
						{/* Edit Mode */}
						<FormGroup sx={{ mb: 3 }}>
							<Label>Name *</Label>
							<InputField
								type="text"
								name="name"
								value={customerName}
								onChange={handleChange('name')}
								required
							/>
						</FormGroup>

						<FormGroup sx={{ mb: 3 }}>
							<Label>Email</Label>
							<InputField
								type="email"
								name="email"
								value={customerEmail}
								onChange={handleChange('email')}
							/>
						</FormGroup>

						<FormGroup sx={{ mb: 3 }}>
							<Label>Phone</Label>
							<InputField
								type="text"
								name="phone"
								value={customerPhone}
								onChange={handleChange('phone')}
							/>
						</FormGroup>

						<FormGroup sx={{ mb: 3 }}>
							<Label>Address</Label>
							<TextArea
								name="address"
								value={customerAddress}
								onChange={handleChange('address')}
								rows={2}
							/>
						</FormGroup>

						<FormGroup sx={{ mb: 3 }}>
							<Label>Notes</Label>
							<TextArea name="notes" value={customerNotes} onChange={handleChange('notes')} rows={3} />
						</FormGroup>

						<CreateButton onClick={handleSave} style={{ marginTop: '20px', width: '100%' }}>
							<CircularProgress
								style={{ visibility: isLoading ? 'visible' : 'hidden', position: 'absolute' }}
								size={15}
								color="inherit"
							/>
							<span style={{ visibility: isLoading ? 'hidden' : 'visible' }}>
								{customer ? 'Update Customer' : 'Create Customer'}
							</span>
						</CreateButton>

						{/* Delete Button - only show for existing customers */}
						{customer && !isNewCustomer && (
							<Button
								onClick={() => setIsDeleteDialogOpen(true)}
								variant="outlined"
								color="error"
								startIcon={<DeleteIcon />}
								style={{
									marginTop: '20px',
									width: '100%',
									borderColor: '#dc2626',
									color: '#dc2626',
								}}
							>
								Delete Customer
							</Button>
						)}
					</>
				)}
			</SidePanel>

			<ConfirmationModal
				isOpen={isDeleteDialogOpen}
				onCancel={() => setIsDeleteDialogOpen(false)}
				onConfirm={handleDelete}
				title="Delete Customer"
				message={`Are you sure you want to delete ${customer?.name}? This action cannot be undone.`}
				confirmText="Delete"
				variant="danger"
				isLoading={isDeleting}
			/>
		</>
	);
};

export default CustomerModal;
